import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';

import Button from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {useShown} from '@pb/kit-hooks-use-shown';

import {useInheritSsoSettings} from '../hooks/useInheritSsoSettings';

import {SsoSettingsInheritanceModal} from './sso-settings-inheritance-modal';

import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';

type Props = {
    disabled: boolean,
    accountId: number,
    parentAccount?: AccountShortInfo,
    hasUnsavedChanges?: boolean,
    onInherit: () => Promise<void> | void,
    onParentSaved: () => Promise<void> | void,
    onBusyChange: (busy: boolean) => void,
    onErrorChange: (error: string | null) => void,
};

export const SsoSettingsInheritance = ({
    disabled, accountId, parentAccount, hasUnsavedChanges, onInherit, onParentSaved, onBusyChange, onErrorChange,
}: Props) => {
    const {shown, onClose, onClick} = useShown();
    const {canEditAccount} = useAdminAccessRight();
    const {setSuccessMessage} = useContext(MessageContext);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const {event} = useInheritSsoSettings({accountId, onParentSaved});

    useEffect(() => {
        onErrorChange(shown ? null : error);
    }, [error, shown, onErrorChange]);

    const inherit = useCallback(async (parent: AccountShortInfo, saveParent = false) => {
        if (disabled || hasUnsavedChanges || busyRef.current || (saveParent && !canEditAccount(accountId))) {
            return;
        }
        busyRef.current = true;
        setBusy(true);
        onBusyChange(true);
        setError(null);
        try {
            const inheritError = await event({...parent, saveParent});
            if (inheritError) {
                setError(inheritError);
            } else {
                await onInherit();
                setSuccessMessage('SSO settings inherited successfully.');
                onClose();
            }
        } finally {
            busyRef.current = false;
            setBusy(false);
            onBusyChange(false);
        }
    }, [
        disabled, hasUnsavedChanges, canEditAccount, accountId, onBusyChange,
        event, onInherit, setSuccessMessage, onClose,
    ]);

    const handleClick = useCallback(() => {
        if (parentAccount) {
            inherit(parentAccount);
        } else {
            setError(null);
            onClick();
        }
    }, [parentAccount, inherit, onClick]);

    const closeModal = useCallback(() => {
        if (!busyRef.current) {
            onClose();
        }
    }, [onClose]);

    return (
        <Group direction="v" gap="s">
            <Button
                variant="Type.SECONDARY"
                onClick={handleClick}
                busy={busy}
                disabled={disabled || busy || hasUnsavedChanges}>
                Inherit Settings
            </Button>
            <Dialog shown={shown} setShown={closeModal}>
                <SsoSettingsInheritanceModal
                    onClose={closeModal}
                    accountId={accountId}
                    onInherit={parent => inherit(parent, true)}
                    busy={busy}
                    canSetParent={canEditAccount(accountId)}
                    error={error}
                />
            </Dialog>
        </Group>
    );
};
